(function () {
  var SECTION_SELECTOR = '.oddit-section-recommended-products';
  var CARD_SELECTOR = '.oddit-section-recommended-products__card';
  var PENDING_CLASS = 'oddit-section-recommended-products--pending';

  /**
   * Pull the section back through the Section Rendering API.
   *
   * Shopify only populates the `recommendations` object when the section is
   * rendered from routes.product_recommendations_url, so on a normal page load
   * the server hands us an empty, hidden carousel. Re-requesting this same
   * section id against that endpoint returns the identical markup with the
   * cards filled in, which we swap in place.
   *
   * The section is left hidden on any path that yields no cards — a failed
   * request, or a product Shopify has no recommendations for.
   */
  function loadRecommendations(section) {
    var track = section.querySelector('.oddit-section-recommended-products__track');
    if (!track) return;

    // Already populated: this is the API response itself, or the theme editor
    // re-rendered a section that had cards. Just wire up the carousel.
    if (track.querySelector(CARD_SELECTOR)) {
      section.classList.remove(PENDING_CLASS);
      initSection(section);
      return;
    }

    var productId = section.getAttribute('data-product-id');
    var sectionId = section.getAttribute('data-section-id');
    if (!productId || !sectionId) return;

    var baseUrl = section.getAttribute('data-recommendations-url') || '/recommendations/products';
    var url =
      baseUrl +
      '?section_id=' + encodeURIComponent(sectionId) +
      '&product_id=' + encodeURIComponent(productId) +
      '&limit=' + encodeURIComponent(section.getAttribute('data-recommendations-limit') || '4') +
      '&intent=' + encodeURIComponent(section.getAttribute('data-recommendations-intent') || 'related');

    fetch(url, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('Recommendations request failed');
        return res.text();
      })
      .then(function (html) {
        var fresh = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector(SECTION_SELECTOR);

        // No cards means Shopify has nothing to recommend. Stay hidden.
        if (!fresh || !fresh.querySelector(CARD_SELECTOR)) return;

        section.innerHTML = fresh.innerHTML;
        section.classList.remove(PENDING_CLASS);

        // Injected .oddit-reveal nodes are observed by nothing and would sit at
        // opacity 0 forever without this.
        if (window.OdditInteractions && typeof window.OdditInteractions.attach === 'function') {
          window.OdditInteractions.attach(section);
        }

        initSection(section);
      })
      .catch(function () {
        // Swallowed: the section stays hidden rather than showing an empty rail.
      });
  }

  function initSection(section) {
    if (!section) return;

    var track = section.querySelector('.oddit-section-recommended-products__track');
    var prevBtn = section.querySelector('.oddit-section-recommended-products__nav--prev');
    var nextBtn = section.querySelector('.oddit-section-recommended-products__nav--next');
    var indicatorList = section.querySelector('[data-carousel-indicators]');
    var indicators = [];

    if (!track) return;

    // A page is one track-width of cards, so the dot count is how many of those
    // the strip holds — 8 cards showing 4 at a time is 2 dots, not 8. It depends
    // on the width, so it is computed here rather than in Liquid, and rebuilt
    // whenever the width changes.
    function getPageCount() {
      var maxScroll = track.scrollWidth - track.clientWidth;
      if (maxScroll <= 1 || track.clientWidth <= 0) return 1;
      return Math.ceil(maxScroll / track.clientWidth) + 1;
    }

    function buildIndicators() {
      if (!indicatorList) return;

      var pages = getPageCount();
      if (indicators.length === pages) return;

      indicatorList.innerHTML = '';
      indicators = [];

      // A single page has nothing to indicate.
      if (pages < 2) return;

      for (var i = 0; i < pages; i++) {
        var dot = document.createElement('span');
        dot.className = 'oddit-section-recommended-products__indicator';
        dot.appendChild(document.createElement('span')).setAttribute('aria-hidden', 'true');
        indicatorList.appendChild(dot);
        indicators.push(dot);
      }

      indicators.forEach(function (dot, i) {
        dot.addEventListener('click', function () {
          goToPage(i);
        });
      });
    }

    function goToPage(index) {
      var maxScroll = track.scrollWidth - track.clientWidth;
      track.scrollTo({
        left: Math.min(index * track.clientWidth, maxScroll),
        behavior: 'smooth'
      });
    }

    // One card + gap = the distance a single arrow click advances the track.
    function getScrollAmount() {
      var firstCard = track.querySelector('.oddit-section-recommended-products__card');
      if (!firstCard) return 328;
      var style = window.getComputedStyle(track);
      var gap = parseInt(style.columnGap || style.gap, 10) || 16;
      return firstCard.getBoundingClientRect().width + gap;
    }

    function scrollByCard(direction) {
      track.scrollBy({
        left: direction * getScrollAmount(),
        behavior: 'smooth'
      });
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        scrollByCard(-1);
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        scrollByCard(1);
      });
    }

    // Reflect scroll position across the controls:
    //  - the active dot is the page the track is scrolled to;
    //  - the prev arrow hides on the first page, the next arrow on the last.
    function updateControls() {
      var maxScroll = track.scrollWidth - track.clientWidth;
      var scrollLeft = track.scrollLeft;

      // 1px tolerance absorbs sub-pixel scroll positions at the extremes.
      var atStart = scrollLeft <= 1;
      var atEnd = scrollLeft >= maxScroll - 1;

      if (indicators.length) {
        // The last page is usually a partial one — the strip stops at maxScroll,
        // short of (pages - 1) full track-widths — so the end is pinned to the
        // last dot rather than rounded to whichever page it lands nearest.
        var activeIndex = atEnd
          ? indicators.length - 1
          : Math.min(Math.round(scrollLeft / track.clientWidth), indicators.length - 1);

        indicators.forEach(function (dot, i) {
          dot.classList.toggle('oddit-section-recommended-products__indicator--active', i === activeIndex);
        });
      }

      if (prevBtn) prevBtn.classList.toggle('oddit-section-recommended-products__nav--hidden', atStart);
      if (nextBtn) nextBtn.classList.toggle('oddit-section-recommended-products__nav--hidden', atEnd);
    }

    track.addEventListener('scroll', function () {
      requestAnimationFrame(updateControls);
    }, { passive: true });

    // Recompute after layout changes (breakpoint switches, CSS applying late).
    // The page count moves with the width, so the dots are rebuilt too.
    window.addEventListener('resize', function () {
      requestAnimationFrame(function () {
        buildIndicators();
        updateControls();
      });
    });

    // Set the initial control state to match the starting scroll position.
    buildIndicators();
    updateControls();

    // Desktop pointer drag-to-scroll. No-ops where the track isn't a horizontal
    // scroller (e.g. the mobile grid layout) and ignores touch pointers so
    // native touch/momentum scrolling is untouched.
    var DRAG_THRESHOLD = 5;
    var drag = { active: false, startX: 0, startScroll: 0, moved: false, pointerId: null };

    function trackIsScrollable() {
      return track.scrollWidth - track.clientWidth > 1;
    }

    // Images and links are natively draggable: pressing on one starts an HTML5
    // drag-and-drop (ghost image and all), which fires pointercancel and aborts
    // the scroll drag before it starts. Killing dragstart is what lets a drag
    // begin on a card rather than only on the gaps between them. The click
    // handler below still decides whether the link navigates.
    track.addEventListener('dragstart', function (e) {
      e.preventDefault();
    });

    track.addEventListener('pointerdown', function (e) {
      if (e.button !== 0 || e.pointerType === 'touch') return;
      if (!trackIsScrollable()) return;
      drag.active = true;
      drag.moved = false;
      drag.startX = e.clientX;
      drag.startScroll = track.scrollLeft;
      drag.pointerId = e.pointerId;
    });

    track.addEventListener('pointermove', function (e) {
      if (!drag.active) return;
      var dx = e.clientX - drag.startX;

      // Capture only once this is unambiguously a drag. Capturing on
      // pointerdown would retarget the trailing click from the card's <a> to
      // this track, so a plain click on a card would never navigate.
      if (!drag.moved && Math.abs(dx) > DRAG_THRESHOLD) {
        drag.moved = true;
        try { track.setPointerCapture(e.pointerId); } catch (err) {}
        track.classList.add('oddit-section-recommended-products__track--dragging');
      }

      track.scrollLeft = drag.startScroll - dx;
      e.preventDefault();
    });

    function endDrag() {
      if (!drag.active) return;
      drag.active = false;
      track.classList.remove('oddit-section-recommended-products__track--dragging');
      if (drag.pointerId != null) {
        if (drag.moved) {
          try { track.releasePointerCapture(drag.pointerId); } catch (err) {}
        }
        drag.pointerId = null;
      }
    }

    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);

    // Swallow the synthetic click that ends a real drag so card links don't
    // navigate when the user was only scrolling.
    track.addEventListener('click', function (e) {
      if (drag.moved) {
        e.preventDefault();
        e.stopPropagation();
        drag.moved = false;
      }
    }, true);
  }

  function init() {
    document.querySelectorAll(SECTION_SELECTOR).forEach(loadRecommendations);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target.querySelector(SECTION_SELECTOR);
    if (section) loadRecommendations(section);
  });
})();
